import { Controller } from "../Controller";
import { FormItemController } from "./FormItemController";
import { MultiMediaController } from "./MultiMediaController";
import { FileController } from "./FileController";
import { Tag } from "../../HTML/Tag";
import { toMap, log, getWebPageService, getAppService } from "../../../../HA";
import { dispatchResponse } from "../../Service/ResponseDispatcher";
// Side-effect : peuple la fabrique d'items (slot dans FormItemController).
import "./ControllerRegistry";

export class FormController extends Controller {

    /**
     * Garde de ré-entrance de la soumission (HA-69). `submitPrimary` fait
     * `.click()` sur le bouton principal ; si celui-ci est un `submit` natif, le
     * clic re-déclenche l'événement `submit` du formulaire — que notre listener
     * ré-route vers `submitPrimary` → `.click()` → … récursion synchrone infinie
     * (stack overflow / double POST). Ce drapeau, posé le temps du `.click()`,
     * fait avaler l'événement `submit` que NOUS provoquons.
     */
    private submitting = false;

    public closeSelection(id: string) {
        getAppService().closeFSApp(id);
    }

    public showSelection(app: any) {
        getAppService().showFSApp(app);
    }

    public processBtnClick(tag: Tag) {
        let valid = true;
        this.getFieldControllers().forEach((ic: FormItemController) => {
            if (ic.isInputItem() && !ic.validate()) { valid = false; }
        });
        if (!valid) { return; }

        const data = new Map();
        const form = new FormData();
        const btn = tag.findElementByAttribute("formid");
        const btnItem: FormItemController = btn
            ? this.getActionControllers().get(btn.getAttribute("formid"))
            : this.getActionControllers().values().next().value;

        if (btn) {
            data.set("formId", btn.getAttribute("formid"));
            form.append("formId", btn.getAttribute("formid"));
        }
        if (btnItem) { btnItem.getTag().setState("loading"); }

        this.getFieldControllers().forEach((ic: FormItemController) => {
            if (ic.isInputItem()) {
                if (ic instanceof MultiMediaController) {
                    const files: any[] = ic.getValue();
                    let i = 1;
                    const medias: any[] = [];
                    files.forEach((file: File | string) => {
                        if (file instanceof File) {
                            form.append("media_" + i, file);
                            ++i;
                        } else {
                            medias.push(file);
                        }
                    });
                    if (medias.length > 0) { form.append("medias", JSON.stringify(medias)); }

                } else if (ic instanceof FileController) {
                    const files: any[] = ic.getValue();
                    let i = 1;
                    const medias: any[] = [];
                    files.forEach((file: File | string) => {
                        if (file instanceof File) {
                            form.append("file_" + i, file);
                            ++i;
                        } else {
                            medias.push(file);
                        }
                    });
                    if (medias.length > 0) { form.append("files", JSON.stringify(medias)); }

                } else {
                    const name = ic.getAttr("n");
                    // Le bouton submit porte déjà le formId (attribut `formid`,
                    // appendé plus haut). Beaucoup de shells embarquent aussi un
                    // input caché name="formId" (vide) : le ré-appender ici
                    // duplique la clé → côté PHP la dernière occurrence (vide)
                    // écrase la bonne → le form ne route plus vers son handler.
                    // On l'ignore dès lors que le formId est déjà présent (le
                    // fallback champ caché reste actif si aucun bouton ne le porte).
                    if (name === "formId" && form.has("formId")) { return; }
                    const value = ic.getValue();
                    data.set(name, value);
                    if (Array.isArray(value)) {
                        // Groupe multi-choix (mchk) : une entrée FormData PAR
                        // valeur cochée — le name se termine par `[]`, PHP
                        // re-matérialise le tableau nativement côté serveur.
                        value.forEach((v: any) => form.append(name, v));
                    } else {
                        form.append(name, value);
                    }
                }
            }
        });

        // Reporte les paramètres d'URL de la page dans le POST : une landing
        // porteuse de jeton (reset mot de passe `?t=…`, changement d'email,
        // invitation) doit renvoyer ce jeton au handler, sinon le form ne
        // soumet que ses champs visibles et le serveur perd le contexte. On
        // n'écrase jamais un champ déjà saisi (l'input utilisateur prime), en
        // cohérence avec le repli query→`d` des chargements de page (App.ts).
        if (typeof window !== "undefined" && window.location && window.location.search) {
            new URLSearchParams(window.location.search).forEach((v, k) => {
                if (!form.has(k)) { form.append(k, v); }
            });
        }

        const setState = (state: string, msg?: string) => {
            if (btnItem) { btnItem.getTag().setState(state, msg); }
        };
        this.getDataRepository().saveForm(form).then((response: Map<any, any>) => {
            // Convention forms HorApp : réponse `{ok, message, redirect, id, …}` (status 200,
            // `ok` discriminant), distincte de la convention action legacy `{todo, msg}`.
            // Sans `todo`, l'ancien routage tombait en `noTodo` → `processAction` qui n'affiche
            // jamais le `message` (email devis envoyé mais aucune notif — ha/522). On la traite
            // en premier pour surfacer la notif (succès ET erreur), quel que soit le shape.
            if (response.has("ok")) {
                this.handleFormResponse(response, setState);
                return;
            }
            // FormController logge `msg` case-par-case (success+error uniquement), pas en
            // amont — d'où `skipMsgLog: true`. Cf. doc ResponseDispatcher.
            dispatchResponse(response, {
                skipMsgLog: true,
                handlers: {
                    success: (r) => {
                        setState("success");
                        this.reset();
                        if (r.get("msg")) { log(r.get("msg")); }
                        // `redirect` (convention forms) prime sur le reload : on
                        // quitte le form pour la page cible (ex. fiche pro forma
                        // créée — ha/512). À défaut, reload de l'app courante.
                        if (r.get("redirect")) {
                            getAppService().processAction(toMap({ t: "redirect", url: r.get("redirect") }));
                        } else if (r.get("id")) {
                            getWebPageService().reloadApp(r);
                        }
                    },
                    app: (r) => {
                        setState("idle");
                        getWebPageService().reloadApp(r);
                    },
                    error: (r) => {
                        setState("error", r.get("msg"));
                        if (r.get("msg")) { log(r.get("msg")); }
                    },
                    default: (r) => {
                        setState("idle");
                        getAppService().processAction(r);
                    },
                },
                noTodo: (r) => {
                    setState("idle");
                    if (r.has("app")) {
                        getWebPageService().showApp(r);
                    } else {
                        getAppService().processAction(r);
                    }
                },
            });
        }).catch((reason: any) => {
            setState("error");
            log(["form submit error", reason]);
        });
    }

    /**
     * Traite une réponse au format forms HorApp `{ok, message, redirect, id, …}`.
     * Surface le `message` (ou `msg`) en notification dans les deux cas — succès ET
     * erreur — pour qu'aucun submit ne reste silencieux (ha/522). Sur succès :
     * reset du form, puis `redirect` (prime, ha/512) sinon `reloadApp` si `id`.
     */
    protected handleFormResponse(r: Map<any, any>, setState: (state: string, msg?: string) => void) {
        const message = r.get("message") ?? r.get("msg");
        if (r.get("ok")) {
            setState("success", message);
            this.reset();
            if (message) { log(message); }
            if (r.get("redirect")) {
                getAppService().processAction(toMap({ t: "redirect", url: r.get("redirect") }));
            } else if (r.get("id")) {
                getWebPageService().reloadApp(r);
            }
        } else {
            setState("error", message);
            if (message) { log(message); }
        }
    }

    protected reset() {
        this.getFieldControllers().forEach((ic: FormItemController) => {
            if (ic.isInputItem()) { ic.reset(); }
        });
    }

    protected initialize(data: Map<any, any>) {
        super.initialize(data);
        this.initItems();
        this.bindEnterSubmit();
    }

    /**
     * Soumission à la touche Entrée (HA-69). Les boutons o1x sont rendus en
     * `type="button"` et routent via `processBtnClick` — le navigateur ne fait
     * donc AUCUNE soumission implicite (Entrée dans un champ ne faisait rien).
     * On rétablit la convention web standard, une fois pour tous les formulaires
     * FormController (connexion, inscription, reset…), sans travail par mockup.
     *
     * - Entrée depuis un champ de saisie → clic du bouton principal.
     * - textarea / contenteditable / Shift|Ctrl|Meta|Alt / IME → ignorés.
     * - `submit` natif intercepté (défensif) : jamais de navigation plein-page (SPA).
     * - Échappatoire : `data-ha-no-enter-submit` sur la racine du formulaire.
     */
    protected bindEnterSubmit() {
        const root = this.getTag().getElement() as HTMLElement | null;
        if (!root || typeof root.addEventListener !== "function") { return; }
        if (root.hasAttribute("data-ha-no-enter-submit")) { return; }
        if (root.hasAttribute("data-ha-enter-bound")) { return; }
        root.setAttribute("data-ha-enter-bound", "1");

        // Soumission implicite native (formulaire portant malgré tout un bouton
        // submit) : on annule la navigation et on route par le moteur.
        root.addEventListener("submit", (e: Event) => {
            e.preventDefault();
            // Ré-entrance : ce `submit` provient de notre propre `.click()` sur un
            // bouton submit natif → déjà en cours de traitement, ne pas re-router.
            if (this.submitting) { return; }
            this.submitPrimary(root);
        });

        root.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key !== "Enter" || e.isComposing) { return; }
            if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) { return; }
            const target = e.target as HTMLElement | null;
            if (!target) { return; }
            const tn = target.tagName;
            // ALLOWLIST D'ABORD : seuls un champ de saisie natif et un <select>
            // soumettent. `textarea`, lien, bouton et hôte de texte riche (div
            // `contenteditable`) sont exclus par cette seule règle — un saut de
            // ligne reste un saut de ligne.
            //
            // Et surtout : NE PAS consulter `isContentEditable` sur un champ natif.
            // Le renderer `txtl` d'o1x pose `contenteditable="true"` SUR L'INPUT
            // lui-même ; le garde « texte riche », qui s'exécutait AVANT cette
            // allowlist, désarmait donc Entrée sur TOUS les champs o1x — la page de
            // connexion comprise (HA-69, recette staging : 0 requête `/ha/form`).
            // Le type d'élément est le critère fiable, pas un attribut d'édition
            // que le moteur pose pour ses propres besoins de rendu.
            if (tn !== "INPUT" && tn !== "SELECT") { return; }
            if (tn === "INPUT") {
                const t = (target as HTMLInputElement).type;
                if (t === "button" || t === "submit" || t === "reset" || t === "file") { return; }
            }
            if (!this.submitPrimary(root)) { return; }
            e.preventDefault();
        });
    }

    /** Déclenche le bouton principal du formulaire. Retourne true si trouvé. */
    protected submitPrimary(root: HTMLElement): boolean {
        const btn = this.findSubmitButton(root);
        if (!btn) { return false; }
        // Les boutons o1x (type="button") portent le listener de clic sur leur
        // wrapper englobant ; `.click()` sur l'élément [ft] bulle jusqu'à lui.
        // Drapeau posé pendant le `.click()` synchrone : si `btn` est un submit
        // natif, l'événement `submit` qu'il re-déclenche est avalé (cf. listener).
        this.submitting = true;
        try {
            btn.click();
        } finally {
            this.submitting = false;
        }
        return true;
    }

    /**
     * Choix générique du bouton principal : marqueur explicite `data-ha-submit`
     * > unique bouton [ft="btn"] > classe primary/btn-primary > dernier bouton.
     * Ignore les boutons désactivés et ceux d'un formulaire imbriqué distinct.
     */
    protected findSubmitButton(root: HTMLElement): HTMLElement | null {
        const explicit = root.querySelector<HTMLElement>("[data-ha-submit]");
        if (explicit) { return explicit; }

        const isNested = (el: Element) =>
            root.tagName === "FORM" && el.closest("form") !== root;
        const candidates = Array.from(
            root.querySelectorAll<HTMLElement>('[ft="btn"], button[type="submit"], input[type="submit"]'),
        ).filter((el) => !isNested(el) && !el.hasAttribute("disabled") && el.getAttribute("e") !== "");

        if (candidates.length === 0) { return null; }
        if (candidates.length === 1) { return candidates[0]; }
        const primary = candidates.find((el) => /\bbtn-primary\b|\bprimary\b/.test(el.className));
        return primary ?? candidates[candidates.length - 1];
    }

    protected initItems() {
        const formi = this.getTag().fetchByAttribute("formi");
        formi.forEach((item: Tag) => {
            this.addItemController(FormItemController.createController(toMap({tag: item, formController: this})));
        });
        // Contrôles substitués IN PLACE par le converter mockup (renderer bare,
        // chrome du mockup conservé) : ils n'ont PAS de root [formi] — sans cette
        // passe ils sont invisibles et leurs valeurs ne sont JAMAIS collectées
        // (POST réduit au seul formId, HA-41). On les découvre par leurs attributs
        // moteur ([ft] + [n]) hors wrappers déjà pris en compte ci-dessus.
        const bare = this.getTag().fetchByAttribute("ft");
        bare.forEach((item: Tag) => {
            const el = item.getElement() as HTMLElement | null;
            if (!el || !el.getAttribute("n")) { return; }
            if (el.closest("[formi]")) { return; }
            this.addItemController(FormItemController.createController(toMap({tag: item, formController: this})));
        });
    }

    protected addItemController(itemController: FormItemController) {
        if (itemController.getItemType() === FormItemController.TYPE_BTN_ITEM) {
            this.getActionControllers().set(itemController.getName(), itemController);
        } else {
            this.getFieldControllers().set(itemController.getName(), itemController);
        }
    }

    protected getFieldControllers(): Map<string, FormItemController> {
        if (this.hasNot("fieldControllers")) {
            this.addToData("fieldControllers", new Map());
        }
        return this.get("fieldControllers");
    }

    protected getActionControllers(): Map<string, FormItemController> {
        if (this.hasNot("actionControllers")) {
            this.addToData("actionControllers", new Map());
        }
        return this.get("actionControllers");
    }

}
